import { useState, useEffect, type ReactNode } from 'react';
import {
  Plus,
  ChevronDown,
  SlidersHorizontal,
  Search,
  CheckCircle2,
  Circle,
  Settings,
  X,
  Archive,
  Upload,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/shared/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Dialog, DialogContent } from '@/shared/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useDebounce } from '@/shared/hooks';
import { AiQuickCreatePanel, AiGoalPanel } from '@/features/ai/public';
import { AiBadge } from '@/shared/components/custom/ai-badge/AiBadge';
import { DownloadTemplateMenu } from '@/shared/components/custom/download-template-menu/DownloadTemplateMenu';
import { TaskFilterPanel } from './TaskFilterPanel';
import { ImportTasksDialog } from './ImportTasksDialog';
import { GroupByMenu } from './GroupByMenu';
import type { BoardView, TaskListViewConfig, GroupByOption, TaskFilters } from '../../types';

interface ActionToolbarProps {
  projectId: string;
  boardId: string;
  config: TaskListViewConfig;
  filters: TaskFilters;
  viewMode: BoardView;
  onGroupByChange: (value: GroupByOption) => void;
  onToggleCompleted: (show: boolean) => void;
  onToggleArchived?: (show: boolean) => void;
  onFiltersChange: (filters: TaskFilters) => void;
  onAddTask?: () => void;
  /**
   * Controls specific to the active view (e.g. Timeline's "Hoy" + "Lapso"),
   * rendered inline in the right-hand group. Keeps a view from needing a second
   * toolbar row just to add one or two of its own controls.
   */
  viewControls?: ReactNode;
}

export function ActionToolbar({
  projectId,
  boardId,
  config,
  filters,
  viewMode,
  onGroupByChange,
  onToggleCompleted,
  onToggleArchived,
  onFiltersChange,
  onAddTask,
  viewControls,
}: ActionToolbarProps) {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();

  const [filterOpen, setFilterOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search);
  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      onFiltersChange({ ...filters, search: debouncedSearch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const activeFilterCount = [
    filters.statuses.length > 0,
    (filters.priorities?.length ?? 0) > 0,
    filters.assigneeIds.length > 0,
    (filters.tagIds?.length ?? 0) > 0,
    !!(filters.dueDateFrom || filters.dueDateTo),
    !!(filters.startDateFrom || filters.startDateTo),
    !!filters.titleValue,
    (filters.groupIds?.length ?? 0) > 0,
    (filters.boardIds?.length ?? 0) > 0,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <>
      <div
        data-testid="tasks-action-toolbar-root"
        className="flex flex-wrap items-center gap-2 py-2"
      >
        {/* ─── LEFT ─────────────────────────────────────────────────────────── */}

        {/* Split create button */}
        <div className="flex">
          {/* The design anchors the quick-create panel under this button, left
              edges aligned — not a centered modal. `onAddTask` is kept in the
              props and still called, but no caller passes it today, so the
              button had no behaviour to displace. */}
          <Popover open={aiOpen} onOpenChange={setAiOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                className="gap-1.5 rounded-r-none"
                onClick={onAddTask}
                data-testid="tasks-action-toolbar-add-task"
              >
                <Plus className="size-4" />
                <span className="hidden md:inline">{t('toolbar.addTask')}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={8}
              className="w-[min(460px,calc(100vw-2rem))] rounded-[10px] p-0"
            >
              <AiQuickCreatePanel
                projectId={projectId}
                boardId={boardId}
                onClose={() => setAiOpen(false)}
              />
            </PopoverContent>
          </Popover>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                className="rounded-l-none border-l border-primary/30 px-1.5"
                data-testid="tasks-action-toolbar-create-more"
                aria-label={t('toolbar.addTask')}
              >
                <ChevronDown className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem data-testid="tasks-create-milestone">
                {t('toolbar.createMore.milestone')}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="tasks-create-event">
                {t('toolbar.createMore.event')}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="tasks-create-section">
                {t('toolbar.createMore.section')}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="tasks-create-goal-plan"
                onSelect={() => {
                  setGoalOpen(true);
                }}
              >
                <span className="flex items-center gap-2">
                  {t('toolbar.createMore.goalPlan')}
                  <AiBadge label={t('toolbar.createMore.goalPlanBadge')} size="sm" />
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Filter button + popover */}
        <div className="group/filter flex items-center">
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={hasActiveFilters ? 'secondary' : 'ghost'}
                size="sm"
                className="gap-1.5"
                data-testid="tasks-action-toolbar-filter"
              >
                <SlidersHorizontal className="size-4" />
                <span className="hidden md:inline">{t('toolbar.filter')}</span>
                {activeFilterCount > 0 && (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
              <TaskFilterPanel
                projectId={projectId}
                boardId={boardId}
                filters={filters}
                onChange={onFiltersChange}
                onClose={() => setFilterOpen(false)}
              />
            </PopoverContent>
          </Popover>

          {hasActiveFilters && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="tasks-action-toolbar-filter-clear"
                  onClick={() =>
                    onFiltersChange({ assigneeIds: [], statuses: [], search: filters.search })
                  }
                  aria-label={t('toolbar.clearFilters')}
                  className="size-7 opacity-0 transition-opacity group-hover/filter:opacity-100"
                >
                  <X className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('toolbar.clearFilters')}</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Search input */}
        <div className="relative flex items-center">
          <Search className="absolute left-2 size-3.5 text-muted-foreground" />
          <Input
            type="text"
            data-testid="tasks-action-toolbar-search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('toolbar.searchPlaceholder')}
            className="h-8 w-40 pl-7 text-xs md:w-48"
          />
        </div>

        {/* ─── RIGHT ────────────────────────────────────────────────────────── */}
        <div className="ml-auto flex items-center gap-1">
          {viewControls}

          {/* Completed toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggleCompleted(!config.showCompleted)}
                data-testid="tasks-action-toolbar-completed-toggle"
                className="gap-1.5"
              >
                {config.showCompleted ? (
                  <CheckCircle2 className="size-4 text-primary" />
                ) : (
                  <Circle className="size-4" />
                )}
                <span className="hidden md:inline">{t('toolbar.completed')}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="md:hidden">{t('toolbar.completed')}</TooltipContent>
          </Tooltip>

          {/* Archived toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggleArchived?.(!config.showArchived)}
                data-testid="tasks-action-toolbar-show-archived"
                className="gap-1.5"
              >
                <Archive className={`size-4 ${config.showArchived ? 'text-primary' : ''}`} />
                <span className="hidden md:inline">{t('toolbar.showArchived')}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="md:hidden">{t('toolbar.showArchived')}</TooltipContent>
          </Tooltip>

          {/* Import button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setImportOpen(true)}
                data-testid="tasks-action-toolbar-import"
                aria-label={t('toolbar.import')}
              >
                <Upload className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('toolbar.import')}</TooltipContent>
          </Tooltip>

          <DownloadTemplateMenu
            csvHref="/templates/tasks-import-template.csv"
            xlsxHref="/templates/tasks-import-template.xlsx"
            csvLabel={t('import.downloadTemplateCsv')}
            xlsxLabel={t('import.downloadTemplateXlsx')}
            triggerLabel={t('toolbar.downloadTemplate')}
            testIdPrefix="tasks-action-toolbar"
          />

          {/* Group by dropdown — hidden on board view */}
          {viewMode !== 'board' && (
            <GroupByMenu
              value={config.groupBy}
              onChange={onGroupByChange}
              triggerTestId="tasks-action-toolbar-groupby"
            />
          )}

          {/* Settings */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/${orgId}/projects/${projectId}/settings`)}
                data-testid="tasks-action-toolbar-settings"
                aria-label={t('toolbar.settings')}
              >
                <Settings className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('toolbar.settings')}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <ImportTasksDialog
        projectId={projectId}
        boardId={boardId}
        open={importOpen}
        onOpenChange={setImportOpen}
      />

      <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
        <DialogContent className="max-h-[90dvh] w-[min(43.125rem,calc(100vw-2rem))] gap-0 overflow-hidden p-0 sm:max-w-[43.125rem]">
          <AiGoalPanel projectId={projectId} boardId={boardId} onClose={() => setGoalOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}
